import { memo, useCallback, useEffect } from 'react'
import { useDispatch, useStore } from 'react-redux'
import { useProviderWebSocket } from './ProviderWebSocket'
import type { ProviderWebSocketEvent } from '../../adapters/types'
import { updateOrderHistory } from '../../reducer/orders.reducer'
import { updatePositions } from '../../reducer/positions.reducer'
import commonActions from '../../actions/common.actions'
import type { Order } from '../../proto/generated/order'
import type { AppDispatch, RootState } from '../../store'

const LOG_PREFIX = '[provider-ws]'

/**
 * Pushes provider websocket events into the redux store.
 * Renders nothing; mount it once, inside ProviderWebSocketProvider.
 *
 * - order_update    → merged into ordersHistory (fields the broker does not
 *                     stream, e.g. clientOrderId / basketId, are kept from the
 *                     existing row).
 * - position_update → merged into positions (deterministic basket id lets the
 *                     update land on the REST row).
 * - alert / admin   → surfaced as an info toast.
 * - market_status   → logged (no store slice consumes it yet).
 */
const ProviderWebSocketStoreSync = () => {
    const ws = useProviderWebSocket()
    const dispatch = useDispatch<AppDispatch>()
    const store = useStore<RootState>()

    const onEvent = useCallback(
        (event: ProviderWebSocketEvent) => {
            const features = ws?.config.features
            switch (event.type) {
                case 'order_update': {
                    if (!features?.orderUpdates) return
                    if (!event.order) {
                        console.warn(LOG_PREFIX, 'order update for unknown security ignored', event.raw)
                        return
                    }
                    const history = (store.getState().ordersStore?.ordersHistory ?? []) as Order[]
                    const existing = history.find((order) => order.orderId === event.order!.orderId)
                    const merged: Order = existing
                        ? {
                              ...existing,
                              ...event.order,
                              // Keep app-side identity/grouping that the broker feed does not carry.
                              clientOrderId: existing.clientOrderId || event.order.clientOrderId,
                              basketId: existing.basketId || event.order.basketId,
                              batchId: existing.batchId || event.order.batchId,
                              strategyGroup: existing.strategyGroup || event.order.strategyGroup,
                              orderType: existing.orderType ?? event.order.orderType,
                              isBasket: existing.isBasket || event.order.isBasket,
                              metadata: event.order.metadata || existing.metadata,
                              created: existing.created || event.order.created,
                              orderDate: existing.orderDate || event.order.orderDate
                          }
                        : event.order
                    dispatch(updateOrderHistory({ updates: [merged] }))
                    return
                }

                case 'position_update': {
                    if (!features?.positionUpdates) return
                    if (!event.position) {
                        console.warn(LOG_PREFIX, 'position update for unknown security ignored', event.raw)
                        return
                    }
                    dispatch(updatePositions({ positions: [event.position], overwrite: false }))
                    return
                }

                case 'alert':
                case 'admin_message': {
                    if (!event.message) return
                    commonActions.setApiError(dispatch, {
                        error: true,
                        message: event.message,
                        errorType: 'info'
                    })
                    return
                }

                case 'market_status':
                    console.log(LOG_PREFIX, 'market status', event.exchange, event.status, event.marketType, event.description)
                    return

                case 'connect_rejected':
                    console.warn(LOG_PREFIX, 'provider websocket rejected the session:', event.reason)
                    return

                case 'stream_ack':
                    console.log(LOG_PREFIX, `${event.stream} updates ${event.subscribed ? 'subscribed' : 'unsubscribed'}`)
                    return

                default:
                    return
            }
        },
        [dispatch, store, ws?.config.features]
    )

    useEffect(() => {
        // Embedded mode: the host publishes order/position updates itself; nothing to sync.
        if (!ws || !ws.config.enabled || ws.status === 'disabled') return
        ws.addListener(onEvent)
        return () => {
            ws.removeListener(onEvent)
        }
    }, [ws, onEvent])

    return null
}

export default memo(ProviderWebSocketStoreSync)
